import { type FC, useState } from "react";
import { Link } from "react-router-dom";
import { PageTitle } from "../../../components/PageTitle";
import {
  type AdminTenant,
  useAdminTenantsApi,
  useDeleteAdminTenantApi,
  useUpdateAdminTenantApi,
} from "../../../hooks/useAdminApi";

export const TenantsPage: FC = () => {
  const { data, loading, error, refetch } = useAdminTenantsApi();
  const { updateTenant } = useUpdateAdminTenantApi();
  const { deleteTenant } = useDeleteAdminTenantApi();

  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const filteredTenants =
    data?.items?.filter(
      tenant =>
        tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tenant.description || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
    ) || [];

  const handleToggleActive = async (tenant: AdminTenant) => {
    setActionLoading(true);
    try {
      await updateTenant(tenant.id, { isActive: !tenant.isActive });
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (tenantId: string) => {
    setActionLoading(true);
    try {
      await deleteTenant(tenantId);
      setDeleteConfirm(null);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>テナント管理</PageTitle>
        <div className="alert alert-error">
          <svg
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>テナント管理</PageTitle>
        <Link to="/admin" className="btn btn-ghost btn-sm">
          ← ダッシュボードに戻る
        </Link>
      </div>

      {/* 検索 */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="テナント名または説明で検索..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="input input-bordered w-full md:w-96"
        />
      </div>

      {/* テーブル */}
      <div className="card bg-base-100 shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>テナント名</th>
                <th>説明</th>
                <th>ユーザー数</th>
                <th>ステータス</th>
                <th>作成日</th>
                <th className="text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    <span className="loading loading-spinner loading-md"></span>
                  </td>
                </tr>
              ) : filteredTenants.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-gray-500">
                    テナントが見つかりません
                  </td>
                </tr>
              ) : (
                filteredTenants.map(tenant => (
                  <tr key={tenant.id} className="hover">
                    <td>
                      <Link
                        to={`/admin/tenants/${tenant.id}`}
                        className="link link-primary font-medium"
                      >
                        {tenant.name}
                      </Link>
                    </td>
                    <td>
                      <span className="text-sm text-gray-600 line-clamp-2">
                        {tenant.description || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-ghost">
                        {tenant.userCount}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          tenant.isActive ? "badge-success" : "badge-error"
                        }`}
                      >
                        {tenant.isActive ? "有効" : "無効"}
                      </span>
                    </td>
                    <td className="text-sm text-gray-500">
                      {new Date(tenant.createdAt).toLocaleDateString("ja-JP")}
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleToggleActive(tenant)}
                          disabled={actionLoading}
                          className={`btn btn-xs ${
                            tenant.isActive ? "btn-warning" : "btn-success"
                          }`}
                        >
                          {tenant.isActive ? "無効化" : "有効化"}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(tenant.id)}
                          disabled={actionLoading}
                          className="btn btn-xs btn-error"
                        >
                          削除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 統計 */}
      <div className="mt-4 text-sm text-gray-600">
        全 {data?.total || 0} 件中 {filteredTenants.length} 件を表示
      </div>

      {/* 削除確認モーダル */}
      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg text-color-title">
              テナントを削除しますか？
            </h3>
            <p className="py-4 text-gray-600">
              この操作は取り消すことができません。テナントに関連する全てのデータが削除されます。
            </p>
            <div className="modal-action">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={actionLoading}
                className="btn btn-ghost"
              >
                キャンセル
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={actionLoading}
                className="btn btn-error"
              >
                {actionLoading ? (
                  <span className="loading loading-spinner loading-sm"></span>
                ) : (
                  "削除する"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => setDeleteConfirm(null)}
          ></div>
        </div>
      )}
    </div>
  );
};

export default TenantsPage;
